/**
 * @file routes/_app/tc/$id.tsx
 * @description TC（交易憑證）文件詳情頁（M3）。
 *
 * 功能：
 * - PageShell 展示 TC 文件所有欄位（code、clientName、certNo、transactionType 等）
 * - 已作廢時顯示 Alert + 作廢原因
 * - 操作按鈕：下載 PDF → window.open('/api/tc/:id/print.pdf')
 * - Admin/Manager：顯示「作廢 TC」按鈕（已作廢的不顯示）
 *
 * 角色：所有登入角色可查看
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Alert, Button, Form, Input, Space, Tag } from 'antd';
import { message, modal } from '@/lib/antd-app';
import { ArrowLeft, Download, Hash, Truck, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import { DataField, DataFieldGrid, PageShell, SectionCard, StatusPill } from '@/components/shell';
import { color, font, radius, space, text, weight } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/tc/$id')({ component: TcDetailPage });

// ─── 類型定義 ─────────────────────────────────────────────────────────────────

/**
 * TC 文件詳情型別（來自 GET /api/tc/:id）。
 */
interface TcDetail {
  id: number;
  code: string;
  certificateId: number;
  clientId: number;
  transactionType: string;
  productItem: string;
  quantity: string;
  unit: string;
  transactionDate: string | null;
  buyerName: string | null;
  buyerTaxId: string | null;
  pdfFileId: number | null;
  issuedBy: number;
  issuedAt: string | null;
  voidedAt: string | null;
  voidedReason: string | null;
  notes: string | null;
  certNo: string;
  clientName: string;
  issuerName: string;
}

// ─── 主頁面組件 ───────────────────────────────────────────────────────────────

/**
 * TC 文件詳情頁主組件。
 */
function TcDetailPage() {
  const { id } = Route.useParams();
  const tcId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user!);
  const canManage = user.roles.includes('admin') || user.roles.includes('manager');

  const [voidForm] = Form.useForm<{ reason: string }>();

  // ─── 查詢 TC 詳情 ────────────────────────────────────────────────
  const { data, isLoading } = useQuery<{ item: TcDetail }>({
    queryKey: ['tc-detail', tcId],
    queryFn: () => apiClient.get(`/api/tc/${tcId}`),
  });

  const tc = data?.item;

  // ─── 作廢 Mutation ────────────────────────────────────────────────
  const voidMutation = useMutation({
    mutationFn: (body: { reason: string }) => apiClient.post(`/api/tc/${tcId}/void`, body),
    onSuccess: () => {
      message.success('TC 文件已作廢');
      void qc.invalidateQueries({ queryKey: ['tc-detail', tcId] });
      void qc.invalidateQueries({ queryKey: ['tc-list'] });
    },
    onError: (e: Error) => {
      message.error(e.message ?? '作廢失敗，請稍後再試');
    },
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <Alert type="info" message="載入中..." showIcon />
      </div>
    );
  }
  if (!tc) {
    return (
      <div className="p-6">
        <Alert type="error" message="TC 文件不存在或無法存取" />
      </div>
    );
  }

  const isVoided = !!tc.voidedAt;

  return (
    <PageShell
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <Truck size={20} color={color.brand.primary} />
          {tc.code}
        </span>
      }
      meta={
        isVoided ? (
          <StatusPill tone="danger">已作廢</StatusPill>
        ) : (
          <StatusPill tone="success">有效</StatusPill>
        )
      }
      actions={
        <Space>
          <Button icon={<ArrowLeft size={14} />} onClick={() => void navigate({ to: '/tc' })}>
            返回列表
          </Button>
          <Button
            icon={<Download size={14} />}
            onClick={() => window.open(`/api/tc/${tcId}/print.pdf`, '_blank')}
          >
            下載 PDF
          </Button>
          {canManage && !isVoided && (
            <Button
              danger
              icon={<Trash2 size={14} />}
              onClick={() => {
                modal.confirm({
                  title: '確認作廢此 TC 文件？',
                  content: (
                    <Form form={voidForm} layout="vertical" style={{ marginTop: 16 }}>
                      <Form.Item
                        name="reason"
                        label="作廢原因"
                        rules={[{ required: true, message: '請填寫作廢原因' }]}
                      >
                        <Input.TextArea rows={3} placeholder="請說明作廢原因..." maxLength={500} />
                      </Form.Item>
                    </Form>
                  ),
                  okText: '確認作廢',
                  cancelText: '取消',
                  okButtonProps: { danger: true },
                  onOk: () => {
                    return new Promise<void>((resolve, reject) => {
                      voidForm
                        .validateFields()
                        .then((values) => {
                          voidMutation.mutate(values, {
                            onSuccess: () => resolve(),
                            onError: reject,
                          });
                        })
                        .catch(reject);
                    });
                  },
                });
              }}
            >
              作廢 TC
            </Button>
          )}
        </Space>
      }
    >
      {/* 已作廢提示 */}
      {isVoided && (
        <Alert
          type="warning"
          showIcon
          message={`此 TC 文件已於 ${new Date(tc.voidedAt!).toLocaleDateString('zh-TW')} 作廢`}
          description={tc.voidedReason ? `原因：${tc.voidedReason}` : undefined}
          style={{ marginBottom: space[5] }}
        />
      )}

      {/* 基本資訊卡片 */}
      <SectionCard title="基本資訊" icon={<Hash size={14} />}>
        {/* 編號 + 狀態列 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: `${space[3]}px ${space[4]}px`,
            background: color.surface.muted,
            borderRadius: radius.md,
            border: `1px solid ${color.border.subtle}`,
            marginBottom: space[5],
          }}
        >
          <span
            style={{
              fontFamily: font.mono,
              fontSize: text.md,
              fontWeight: weight.semibold,
              color: color.text.primary,
            }}
          >
            {tc.code}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Tag>{tc.transactionType}</Tag>
            {isVoided ? (
              <StatusPill tone="danger">已作廢</StatusPill>
            ) : (
              <StatusPill tone="success">有效</StatusPill>
            )}
          </div>
        </div>

        <DataFieldGrid columns={3}>
          <DataField label="客戶名稱" value={tc.clientName} />
          <DataField label="關聯證書號碼" value={tc.certNo} mono />
          <DataField label="核發人員" value={tc.issuerName} />
          <DataField label="產品項目" value={tc.productItem} span={2} />
          <DataField label="數量" value={`${tc.quantity} ${tc.unit}`} mono />
          <DataField
            label="交易日期"
            value={
              tc.transactionDate ? new Date(tc.transactionDate).toLocaleDateString('zh-TW') : '—'
            }
            mono
          />
          <DataField
            label="核發時間"
            value={tc.issuedAt ? new Date(tc.issuedAt).toLocaleString('zh-TW') : '—'}
            mono
          />
          {tc.notes && <DataField label="備注" value={tc.notes} span={3} />}
        </DataFieldGrid>
      </SectionCard>

      {/* 買受方資訊 */}
      {(tc.buyerName || tc.buyerTaxId) && (
        <div style={{ marginTop: space[5] }}>
          <SectionCard title="買受方資訊">
            <DataFieldGrid columns={2}>
              <DataField label="買受方名稱" value={tc.buyerName ?? '—'} />
              <DataField label="統一編號" value={tc.buyerTaxId ?? '—'} mono />
            </DataFieldGrid>
          </SectionCard>
        </div>
      )}
    </PageShell>
  );
}
